// MwSt Service - src/services/mwstService.js

const MWST_SATZ = 8.1; // 8.1% MwSt

// MwSt-Beträge berechnen
export const calculateMwstAmounts = (
  betrag,
  mwstSatz = MWST_SATZ,
  istBrutto = true
) => {
  const satz = mwstSatz / 100;

  if (istBrutto) {
    // Betrag enthält bereits MwSt
    const nettoBetrag = betrag / (1 + satz);
    const mwstBetrag = betrag - nettoBetrag;

    return {
      bruttoBetrag: betrag,
      nettoBetrag: Math.round(nettoBetrag * 100) / 100,
      mwstBetrag: Math.round(mwstBetrag * 100) / 100,
      mwstSatz: mwstSatz,
    };
  } else {
    // Betrag ist Netto, MwSt addieren
    const mwstBetrag = betrag * satz;
    const bruttoBetrag = betrag + mwstBetrag;

    return {
      bruttoBetrag: Math.round(bruttoBetrag * 100) / 100,
      nettoBetrag: betrag,
      mwstBetrag: Math.round(mwstBetrag * 100) / 100,
      mwstSatz: mwstSatz,
    };
  }
};

// Quartal aus Datum ermitteln
export const getQuartalFromDate = (datum) => {
  const date = new Date(datum);
  const year = date.getFullYear();
  const month = date.getMonth() + 1; // 0-11 -> 1-12

  const quarter = Math.ceil(month / 3);
  return `${year}-Q${quarter}`;
};

// Fälligkeitsdatum für MwSt-Quartal berechnen
export const getMwstFaelligkeitsdatum = (quartal) => {
  const [year, q] = quartal.split("-Q");
  const quarter = parseInt(q);

  // Einen Monat nach Quartalsende
  const faelligkeitsMonat = quarter * 3 + 1;

  if (faelligkeitsMonat <= 12) {
    // Gleices Jahr
    return new Date(
      parseInt(year),
      faelligkeitsMonat - 1,
      getDaysInMonth(parseInt(year), faelligkeitsMonat)
    );
  } else {
    // Nächstes Jahr (Q4 -> Januar nächstes Jahr)
    return new Date(parseInt(year) + 1, 0, 31); // 31. Januar
  }
};

// Hilfsfunktion: Tage im Monat
const getDaysInMonth = (year, month) => {
  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  // Schaltjahr prüfen
  if (
    month === 2 &&
    ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0)
  ) {
    return 29;
  }

  return daysInMonth[month - 1];
};
// MwSt-Saldo für Quartal berechnen. Rechnet ausschliesslich aus den
// Budget-Einträgen (mwst_satz/mwst_betrag je Eintrag) — unabhängig davon, ob
// das Quartal bereits (teilweise) vergangen ist oder ein Eintrag schon bezahlt
// wurde. Eine frühere Variante rechnete den bereits verstrichenen Teil des
// laufenden bzw. vergangener Quartale stattdessen blind aus den Bank-Buchungen
// (jede Ausgabe an ein Konto >= 4000 als abzugsfähige Vorsteuer, auch Löhne/
// Mieten/Versicherungen) — das lieferte strukturell falsche, teils gegenläufige
// Salden, weil das Journal keine MwSt-Zuordnung je Buchung kennt.
export const calculateQuartalsMwst = (budgetEntries, quartal) => {
  const [year, q] = quartal.split("-Q");
  const quarter = parseInt(q);

  // Quartals-Grenzen
  const startMonth = (quarter - 1) * 3 + 1;
  const endMonth = quarter * 3;
  const startDate = new Date(parseInt(year), startMonth - 1, 1);
  const endDate = new Date(parseInt(year), endMonth, 0); // Letzter Tag des Monats

  let geschuldeteMwst = 0; // MwSt auf Einnahmen
  let bezahlteMwst = 0; // MwSt auf Ausgaben

  budgetEntries.forEach((entry) => {
    if (!entry.mwst_satz || entry.mwst_satz === 0) return;

    const entryDates = getEntryDatesInPeriod(entry, startDate, endDate);

    entryDates.forEach(() => {
      const mwstBetrag = entry.mwst_betrag || 0;

      if (entry.typ === "Einnahme") {
        geschuldeteMwst += mwstBetrag;
      } else {
        bezahlteMwst += mwstBetrag;
      }
    });
  });

  const saldo = geschuldeteMwst - bezahlteMwst;

  return {
    quartal,
    geschuldeteMwst: Math.round(geschuldeteMwst * 100) / 100,
    bezahlteMwst: Math.round(bezahlteMwst * 100) / 100,
    saldo: Math.round(saldo * 100) / 100,
    faelligAm: getMwstFaelligkeitsdatum(quartal),
  };
};

// Termine eines Budget-Eintrags in einem Zeitraum ermitteln
const getEntryDatesInPeriod = (entry, startDate, endDate) => {
  const dates = [];

  if (entry.typ_timing === "Einmalig") {
    const entryDate = new Date(entry.wann);
    if (entryDate >= startDate && entryDate <= endDate) {
      dates.push(entryDate);
    }
  } else {
    // Regelmässige Einträge
    const entryStartDate = new Date(entry.ab_wann);
    const entryEndDate = entry.bis_wann ? new Date(entry.bis_wann) : endDate;

    const rangeStart = new Date(
      Math.max(entryStartDate.getTime(), startDate.getTime())
    );
    const rangeEnd = new Date(
      Math.min(entryEndDate.getTime(), endDate.getTime())
    );

    if (rangeStart <= rangeEnd) {
      const occurrences = calculateRecurringOccurrences(
        rangeStart,
        rangeEnd,
        entry.haeufigkeit
      );
      dates.push(...occurrences);
    }
  }

  return dates;
};

// Wiederkehrende Termine berechnen - preserves original day across months
const calculateRecurringOccurrences = (startDate, endDate, frequency) => {
  const occurrences = [];
  const origDay = startDate.getDate();
  let monthOffset = 0;

  while (true) {
    let nextDate;
    switch (frequency) {
      case "monatlich":
        nextDate = new Date(startDate.getFullYear(), startDate.getMonth() + monthOffset, 1);
        nextDate.setDate(Math.min(origDay, new Date(nextDate.getFullYear(), nextDate.getMonth() + 1, 0).getDate()));
        monthOffset += 1;
        break;
      case "quartalsweise":
        nextDate = new Date(startDate.getFullYear(), startDate.getMonth() + monthOffset, 1);
        nextDate.setDate(Math.min(origDay, new Date(nextDate.getFullYear(), nextDate.getMonth() + 1, 0).getDate()));
        monthOffset += 3;
        break;
      case "jährlich":
        nextDate = new Date(startDate.getFullYear(), startDate.getMonth() + monthOffset, 1);
        nextDate.setDate(Math.min(origDay, new Date(nextDate.getFullYear(), nextDate.getMonth() + 1, 0).getDate()));
        monthOffset += 12;
        break;
      default:
        return occurrences;
    }

    if (nextDate > endDate) break;
    occurrences.push(nextDate);
  }

  return occurrences;
};

// MwSt-Cashflow-Einträge für alle Quartale generieren
export const generateMwstCashflowEntries = (
  budgetEntries,
  startDate,
  endDate
) => {
  const mwstEntries = [];
  const quartale = getQuartaleInPeriod(startDate, endDate);

  quartale.forEach((quartal) => {
    const mwstData = calculateQuartalsMwst(budgetEntries, quartal);

    if (mwstData.saldo > 0) {
      // Nur wenn Nachzahlung erforderlich
      mwstEntries.push({
        name: `§ ${quartal}`,
        typ: "Ausgabe",
        betrag: mwstData.saldo,
        wann: mwstData.faelligAm.toISOString().split("T")[0],
        kategorie: "Steuern",
        projekt: "",
        konto: "2200", // Geschuldete Steuern
        typ_timing: "Einmalig",
        mwst_satz: 0, // MwSt-Zahlung selbst ist steuerfrei
        ist_brutto: true,
        ist_mwst_abrechnung: true,
      });
    }
  });

  return mwstEntries;
};

// Alle Quartale in einem Zeitraum ermitteln
const getQuartaleInPeriod = (startDate, endDate) => {
  const quartale = [];
  const start = new Date(startDate);
  const end = new Date(endDate);

  let currentYear = start.getFullYear();
  const endYear = end.getFullYear();

  while (currentYear <= endYear) {
    for (let q = 1; q <= 4; q++) {
      const quartal = `${currentYear}-Q${q}`;
      const quarterStart = new Date(currentYear, (q - 1) * 3, 1);
      const quarterEnd = new Date(currentYear, q * 3, 0);

      // Prüfe ob Quartal im gewünschten Zeitraum liegt
      if (quarterEnd >= start && quarterStart <= end) {
        quartale.push(quartal);
      }
    }
    currentYear++;
  }

  return quartale;
};

// MwSt-Übersicht für alle vier Quartale des Jahres von currentDate — vergangene,
// laufende und künftige Quartale werden einheitlich aus den Budget-Einträgen
// berechnet (siehe calculateQuartalsMwst).
export const calculateAllQuartersMwst = async (
  budgetEntries,
  currentDate = new Date()
) => {
  const currentYear = currentDate.getFullYear();
  const allQuarters = [];

  for (let q = 1; q <= 4; q++) {
    const quartal = `${currentYear}-Q${q}`;
    allQuarters.push(calculateQuartalsMwst(budgetEntries, quartal));
  }

  return allQuarters;
};

export const generateDynamicMwstCashflowEntries = async (
  budgetEntries,
  startDate,
  endDate
) => {
  const mwstEntries = [];

  // Finde alle Quartale im Zeitraum
  const quartalsInPeriod = getQuartaleInPeriod(startDate, endDate);

  for (const quartal of quartalsInPeriod) {
    const faelligkeitsDatum = getMwstFaelligkeitsdatum(quartal);

    // Prüfe ob Fälligkeitsdatum im gewünschten Zeitraum liegt
    if (faelligkeitsDatum >= startDate && faelligkeitsDatum <= endDate) {
      const mwstSaldo = calculateQuartalsMwst(budgetEntries, quartal).saldo;

      // Erstelle Cashflow-Eintrag: Nachzahlung (Ausgabe) oder Rückerstattung (Einnahme)
      if (mwstSaldo > 0) {
        mwstEntries.push({
          name: `MwSt-Abrechnung ${quartal}`,
          typ: "Ausgabe",
          betrag: Math.round(mwstSaldo * 100) / 100,
          wann: faelligkeitsDatum.toISOString().split("T")[0],
          kategorie: "Steuern",
          projekt: "",
          konto: "2200",
          typ_timing: "Einmalig",
          mwst_satz: 0,
          ist_brutto: true,
          ist_mwst_abrechnung: true,
          quartal: quartal,
          isDynamic: true,
        });
      } else if (mwstSaldo < 0) {
        mwstEntries.push({
          name: `MwSt-Rückerstattung ${quartal}`,
          typ: "Einnahme",
          betrag: Math.round(Math.abs(mwstSaldo) * 100) / 100,
          wann: faelligkeitsDatum.toISOString().split("T")[0],
          kategorie: "Steuern",
          projekt: "",
          konto: "2200",
          typ_timing: "Einmalig",
          mwst_satz: 0,
          ist_brutto: true,
          ist_mwst_abrechnung: true,
          quartal: quartal,
          isDynamic: true,
        });
      }
    }
  }

  return mwstEntries;
};

// Bestehende Funktionen bleiben gleich...
